import { ROUTE_PATHS } from "../../enums/routing-enums";
import { CATEGORY } from "../../enums/tealium-enums";
import type { NavItem } from "../../../components/Navigation/types";
import type { FlyoutMenuItem } from "../../../components/Navigation/CategoryFlyoutMenu/CategoryFlyoutMenu";
import type { NavigationApiResponse, NavigationReportItem } from "../../../services/MyBizShell/mybizShellAPI.types";
import type { LearnMore } from "../../interfaces/config";
import defaultContent from "../../../content/navigation";
import { getNavKeyForIdentity } from "../Incentives/IncentiveRegistry";

interface NavigationConfig {
  performanceIncome: NavItem[];
  groupMgmtNavItems: NavItem[];
  groupMgmtActionReports: NavItem[];
  academy: NavItem | null;
  // Desktop flyout structures
  performanceIncomeFlyout: FlyoutMenuItem[];
  groupManagementFlyout: FlyoutMenuItem[];
  academyFlyout: FlyoutMenuItem | null;
}

const NAV_ITEM_MAP: Record<string, NavItem> = {
  performanceDashboard: {
    id: "performanceDashboard",
    contentKey: "perfIncomeDashboardLink",
    label: defaultContent.linkTitle.perfIncomeDashboardLink,
    entryPage: "performanceDashboard",
    path: ROUTE_PATHS.PERFORMANCE_DASHBOARD,
  },
  pathToBronze: {
    id: "pathToBronze",
    label: defaultContent.linkTitle.newAboActivation,
    entryPage: "pathToBronze",
    path: ROUTE_PATHS.NEW_PATH_TO_BRONZE,
  },
  strongStartIncentive: {
    id: "strongStartIncentive",
    label: defaultContent.linkTitle.strongStartIncentive,
    entryPage: "strongStartIncentive",
    path: ROUTE_PATHS.STRONG_START_INCENTIVE,
  },
  csi: {
    id: "csi",
    label: defaultContent.linkTitle.csi,
    entryPage: "csi",
    path: ROUTE_PATHS.CSI,
  },
  bronzeFoundation: {
    id: "bronzeFoundation",
    label: defaultContent.linkTitle.bronzeFoundation,
    entryPage: "bronzeFoundation",
    path: ROUTE_PATHS.NEW_BRONZE_FOUNDATION,
  },
  bronzeBuilder: {
    id: "bronzeBuilder",
    label: defaultContent.linkTitle.bronzeBuilder,
    entryPage: "bronzeBuilder",
    path: ROUTE_PATHS.NEW_BRONZE_BUILDER,
  },
  performancePlus: {
    id: "performancePlus",
    label: defaultContent.linkTitle.performancePlus,
    entryPage: "performancePlus",
    path: ROUTE_PATHS.PERFORMANCE_PLUS,
  },
  monthlyQ: {
    id: "monthlyQ",
    label: defaultContent.linkTitle.monthlyQ,
    entryPage: "monthlyQ",
    path: ROUTE_PATHS.MONTHLY_Q,
  },
  performancePlusElite: {
    id: "performancePlusElite",
    label: defaultContent.linkTitle.performancePlusElite,
    entryPage: "performancePlusElite",
    path: ROUTE_PATHS.PPE,
  },
  personalGrowthMonthly: {
    id: "personalGrowthMonthly",
    label: defaultContent.linkTitle.personalGrowthMonthly,
    entryPage: "personalGrowthMonthly",
    path: ROUTE_PATHS.MONTHLY_PGG,
  },
  earningsCalculator: {
    id: "earningsCalculator",
    label: defaultContent.linkTitle.earningsCalculator,
    entryPage: "earningsCalculator",
    path: ROUTE_PATHS.EARNINGS_CALCULATOR,
  },
  mbi: {
    id: "mbi",
    label: defaultContent.linkTitle.mbi,
    entryPage: "mbi",
    path: ROUTE_PATHS.MBI,
  },
  qcg: {
    id: "qcg",
    label: defaultContent.linkTitle.qcg,
    entryPage: "qcg",
    path: ROUTE_PATHS.QC_GROWTH,
  },
  personalGroupGrowth: {
    id: "personalGroupGrowth",
    label: defaultContent.linkTitle.personalGroupGrowth,
    entryPage: "personalGroupGrowth",
    path: ROUTE_PATHS.ANNUAL_PGG,
  },
  frontlineGrowth: {
    id: "frontlineGrowth",
    label: defaultContent.linkTitle.frontlineGrowth,
    entryPage: "frontlineGrowth",
    path: ROUTE_PATHS.FGG,
  },
  healthWellbeing: {
    id: "healthWellbeing",
    label: defaultContent.linkTitle.healthWellbeing,
    entryPage: "healthWellbeing",
    path: ROUTE_PATHS.HEALTH_WELLBEING,
  },
  personalGroupGrowthGCR: {
    id: "personalGroupGrowthGCR",
    label: defaultContent.linkTitle.personalGroupGrowthGCR,
    entryPage: "personalGroupGrowthGCR",
    path: ROUTE_PATHS.PGG_GCR,
  },
  visionBonus: {
    id: "visionBonus",
    label: defaultContent.linkTitle.visionBonus,
    entryPage: "visionBonus",
    path: ROUTE_PATHS.VISION_BONUS,
  },
  twoTimeCashIncentive: {
    id: "twoTimeCashIncentive",
    label: defaultContent.linkTitle.twoTimeCashIncentive,
    entryPage: "twoTimeCashIncentive",
    path: ROUTE_PATHS.TWO_TIME_CASH_INCENTIVE,
  },
  hat: {
    id: "hat",
    label: defaultContent.linkTitle.hat,
    entryPage: "hat",
    path: ROUTE_PATHS.HAT,
  },
  faa: {
    id: "faa",
    label: defaultContent.linkTitle.faa,
    entryPage: "faa",
    path: ROUTE_PATHS.FAA,
  },
  leadershipSeminar: {
    id: "leadershipSeminar",
    label: defaultContent.linkTitle.leadershipSeminar,
    entryPage: "leadershipSeminar",
    path: ROUTE_PATHS.LEADERSHIP_SEMINAR,
  },
  lsPathView: {
    id: "lsPathView",
    label: defaultContent.linkTitle.leadershipSeminar,
    entryPage: "leadershipSeminar",
    path: ROUTE_PATHS.LEADERSHIP_SEMINAR,
  },
  annualq: {
    id: "annualq",
    label: defaultContent.linkTitle.annualq,
    entryPage: "annualq",
    path: ROUTE_PATHS.ANNUALQ,
  },
  frontlineQ: {
    id: "frontlineQ",
    label: defaultContent.linkTitle.frontlineQ,
    entryPage: "frontlineQ",
    path: ROUTE_PATHS.FRONTLINE_Q,
  },
  leadershipPlus: {
    id: "leadershipPlus",
    label: defaultContent.linkTitle.leadershipPlus,
    entryPage: "leadershipPlus",
    path: ROUTE_PATHS.LEADERSHIP_PLUS,
  },
  groupManagementDashboard: {
    id: "groupManagementDashboard",
    contentKey: "groupManagementActionCenterLink",
    label: defaultContent.linkTitle.groupManagementActionCenterLink,
    entryPage: "groupManagementDashboard",
    path: ROUTE_PATHS.GROUP_MANAGEMENT_DASHBOARD,
  },
  los: {
    id: "los",
    contentKey: "los",
    label: defaultContent.linkTitle.los,
    entryPage: "los",
    path: ROUTE_PATHS.LOS,
  },
  edlos: {
    id: "edlos",
    label: defaultContent.linkTitle.edlos,
    entryPage: "edlos",
    path: ROUTE_PATHS.EDLOS,
  },
  watchlist: {
    id: "watchlist",
    label: defaultContent.linkTitle.watchlist,
    entryPage: "watchlist",
    path: ROUTE_PATHS.WATCHLIST,
  },
  customerMemberList: {
    id: "customerMemberList",
    label: defaultContent.linkTitle.customerMemberList,
    entryPage: "customerMemberList",
    path: ROUTE_PATHS.CUSTOMER_LIST,
  },
  ssiUplineReport: {
    id: "ssiUplineReport",
    label: defaultContent.linkTitle.ssiUplineReport,
    entryPage: "ssiUplineReport",
    path: ROUTE_PATHS.SSI_UPLINE_REPORT,
  },
  bronzeGroupTracking: {
    id: "bronzeGroupTracking",
    label: defaultContent.linkTitle.bronzeGroupTracking,
    entryPage: "bronzeGroupTracking",
    path: ROUTE_PATHS.BRONZE_GROUP_TRACKING,
  },
  groupSopTracking: {
    id: "groupSopTracking",
    label: defaultContent.linkTitle.groupSopTracking,
    entryPage: "groupSopTracking",
    path: ROUTE_PATHS.SOP_REPORT,
  },
  whosNew: {
    id: "whosNew",
    label: defaultContent.linkTitle.whosNew,
    entryPage: "whosNew",
    path: ROUTE_PATHS.WHOS_NEW_ACTION_REPORT,
  },
  whosClose: {
    id: "whosClose",
    label: defaultContent.linkTitle.whosClose,
    entryPage: "whosClose",
    path: ROUTE_PATHS.WHOS_CLOSE_ACTION_REPORT,
  },
  birthdays: {
    id: "birthdays",
    label: defaultContent.linkTitle.birthdays,
    entryPage: "birthdays",
    path: ROUTE_PATHS.BIRTHDAYS_ACTION_REPORT,
  },
  renewals: {
    id: "renewals",
    label: defaultContent.linkTitle.renewals,
    entryPage: "renewals",
    path: ROUTE_PATHS.RENEWALS,
  },
  nonBonusableBv: {
    id: "nonBonusableBv",
    label: defaultContent.linkTitle.nonBonusableBv,
    entryPage: "nonBonusableBv",
    path: ROUTE_PATHS.NON_BONUSABLE_BV_ACTION_REPORT,
  },
  cvrCompliance: {
    id: "cvrCompliance",
    label: defaultContent.linkTitle.cvrCompliance,
    entryPage: "cvrCompliance",
    path: ROUTE_PATHS.CVR,
  },
  iboOnboarding: {
    id: "iboOnboarding",
    label: defaultContent.linkTitle.iboOnboarding,
    entryPage: "iboOnboarding",
    path: ROUTE_PATHS.IBO_ONBOARDING,
  },
  cbrClockCheck: {
    id: "cbrClockCheck",
    label: defaultContent.linkTitle.cbrClockCheck,
    entryPage: "cbrClockCheck",
    path: ROUTE_PATHS.CBR,
  },
  diamondBonus: {
    id: "diamondBonus",
    label: defaultContent.linkTitle.diamondBonus,
    entryPage: "diamondBonus",
    path: ROUTE_PATHS.DIAMOND_BONUS,
  },
  pathToA70: {
    id: "pathToA70",
    label: defaultContent.linkTitle.pathToA70,
    entryPage: "pathToA70",
    path: ROUTE_PATHS.PATH_TO_A70,
  },
  a70EliteBonus: {
    id: "a70EliteBonus",
    label: defaultContent.linkTitle.a70EliteBonus,
    entryPage: "a70EliteBonus",
    path: ROUTE_PATHS.A70_ELITE_BONUS,
  },
  onlineStatements: {
    id: "onlineStatements",
    label: defaultContent.linkTitle.onlineStatements,
    entryPage: "onlineStatements",
    path: ROUTE_PATHS.ONLINE_STATEMENTS,
  },
  qGrowthIncentive: {
    id: "qGrowthIncentive",
    label: defaultContent.linkTitle.qGrowthIncentive,
    entryPage: "qGrowthIncentive",
    path: ROUTE_PATHS.Q_GROWTH_INCENTIVE,
  },
  earlyQBooster: {
    id: "earlyQBooster",
    label: defaultContent.linkTitle.qGrowthIncentive,
    entryPage: "earlyQBooster",
    contentKey: "qGrowthIncentive",
    path: ROUTE_PATHS.EARLY_Q_BOOSTER,
  },
  retroPvInquiry: {
    id: "retroPvInquiry",
    label: defaultContent.linkTitle.retroPvInquiry,
    entryPage: "retroPvInquiry",
    path: ROUTE_PATHS.RETRO_PV_INQUIRY,
  },
  saqCertification: {
    id: "saqCertification",
    label: (defaultContent.linkTitle as Record<string, string>).saqCertification,
    entryPage: "saqCertification",
    path: ROUTE_PATHS.SAQ_ACTION_REPORT,
  },
  foundersSalesIncentive: {
    id: "foundersSalesIncentive",
    label: (defaultContent.linkTitle as Record<string, string>).foundersSalesIncentive,
    entryPage: "foundersSalesIncentive",
    path: ROUTE_PATHS.FOUNDERS_SALES_INCENTIVE,
  },
};

// Maps backend ContentKey values to frontend labels.
// Used when the backend needs to override the default NAV_ITEM_MAP label (e.g. GCR PGG, annual PGG).
const CONTENT_KEY_LABEL_MAP: Record<string, string> = {
  pgg: defaultContent.linkTitle.personalGroupGrowth,
  pggAnnual: defaultContent.linkTitle.personalGrowthAnnual,
  pgti: defaultContent.linkTitle.personalGroupTieredIncentive,
};

// Maps backend ContentKey values to frontend i18n keys.
// Required so the correct translation is looked up rather than the item's own ID.
// e.g. "personalGroupGrowthGCR" would resolve to "Personal Group Growth GCR" — wrong.
// With contentKey "personalGroupGrowth" it resolves to "Personal Group Growth+" — correct.
const CONTENT_KEY_TO_I18N_KEY: Record<string, string> = {
  pgg: "personalGroupGrowth",
  pggAnnual: "personalGrowthAnnual",
  pgti: "personalGroupTieredIncentive",
};

const resolveOverrides = (
  item: NavItem,
  { ContentKey, RouteID }: NavigationReportItem,
): { label: string; path: string; contentKey?: string } => ({
  label: ContentKey ? (CONTENT_KEY_LABEL_MAP[ContentKey] ?? item.label) : item.label,
  path: RouteID ? `/${RouteID}` : item.path,
  ...(ContentKey && CONTENT_KEY_TO_I18N_KEY[ContentKey] ? { contentKey: CONTENT_KEY_TO_I18N_KEY[ContentKey] } : {}),
});

const resolveMarketOverrides = (item: NavItem, aff?: string): Partial<NavItem> => {
  if (item.id !== "leadershipSeminar" && item.id !== "lsPathView") return {};

  const contentKey = getNavKeyForIdentity({ id: "leadershipSeminar" }, { aff });
  return {
    contentKey,
    label: defaultContent.linkTitle[contentKey],
  };
};

const mapItems = (ids: NavigationReportItem[], aff?: string): NavItem[] =>
  ids.reduce<NavItem[]>((acc, reportItem) => {
    const item = NAV_ITEM_MAP[reportItem.Id];
    if (!item) {
      // eslint-disable-next-line no-console
      console.warn(`[createNavigationConfig] Unknown navigation ID: "${reportItem.Id}"`);
      return acc;
    }
    acc.push({ ...item, ...resolveOverrides(item, reportItem), ...resolveMarketOverrides(item, aff) });
    return acc;
  }, []);

const mapItemsToFlyout = (ids: NavigationReportItem[], aff?: string): FlyoutMenuItem[] =>
  ids.reduce<FlyoutMenuItem[]>((acc, reportItem) => {
    const item = NAV_ITEM_MAP[reportItem.Id];
    if (!item) {
      return acc;
    }
    acc.push({
      id: item.id,
      contentKey: item.contentKey,
      ...resolveOverrides(item, reportItem),
      ...resolveMarketOverrides(item, aff),
    });
    return acc;
  }, []);

/**
 * Maps route IDs to their corresponding dashboardLinks.learnMore keys.
 * Only routes with learn more links need to be mapped here.
 * Keys must exist in the config repo's learnMore configuration.
 */
const LEARN_MORE_KEY_MAP: Record<string, keyof LearnMore> = {
  annualq: "annualQ",
  bronzeBuilder: "bronzeBuilder",
  bronzeFoundation: "bronzeFoundation",
  csi: "customerSalesIncentive",
  diamondBonus: "diamondBonus",
  foundersSalesIncentive: "foundersSalesIncentive",
  frontlineGrowth: "frontlineGrowth",
  frontlineQ: "frontlineGrowth", // FrontlineQ uses frontlineGrowth learn more link
  leadershipSeminar: "leadershipSeminar",
  lsPathView: "leadershipSeminar",
  pathToBronze: "pathToBronze",
  performanceDashboard: "performanceDashboard",
  performancePlus: "performancePlus",
  performancePlusElite: "performancePlusElite",
  personalGrowthMonthly: "personalGroupGrowth",
  personalGroupGrowth: "personalGroupGrowth",
  personalGroupGrowthGCR: "personalGroupGrowth",
  qcg: "qcGrowth",
  qGrowthIncentive: "qGrowthIncentive",
  earlyQBooster: "qGrowthIncentive",
  strongStartIncentive: "strongStartIncentive",
  twoTimeCashIncentive: "twoTimeCashIncentive",
  visionBonus: "visionBonus",
  // Note: monthlyQPlatinum and strongStartIncentiveV2 exist in config but no matching route ID
  // Note: healthWellbeing and monthlyQ have no learnMore keys in config
};

/**
 * Maps route IDs to their corresponding Tealium CATEGORY for learn more tracking.
 * Only routes with learn more links that need tracking should be mapped here.
 */
const TEALIUM_CATEGORY_MAP: Record<string, CATEGORY> = {
  annualq: CATEGORY.ANNUAL_Q_INCENTIVE,
  bronzeBuilder: CATEGORY.BRONZE_BUILDER_INCENTIVE,
  bronzeFoundation: CATEGORY.BRONZE_FOUNDATION_INCENTIVE,
  csi: CATEGORY.CORE_PLUS_PORTAL,
  diamondBonus: CATEGORY.DIAMOND_BONUS,
  foundersSalesIncentive: CATEGORY.FOUNDERS_SALES_INCENTIVE,
  frontlineGrowth: CATEGORY.FRONTLINE_GROUP_GROWTH,
  frontlineQ: CATEGORY.Frontline_Q,
  leadershipSeminar: CATEGORY.LEADERSHIP_SEMINAR,
  lsPathView: CATEGORY.LEADERSHIP_SEMINAR,
  pathToBronze: CATEGORY.PATH_TO_BRONZE_INCENTIVE,
  performanceDashboard: CATEGORY.MY_PERFORMANCE_DASH,
  performancePlus: CATEGORY.PERFORMANCE_PLUS,
  performancePlusElite: CATEGORY.PERFORMANCE_PLUS_ELITE,
  personalGrowthMonthly: CATEGORY.PERSONAL_GROUP_GROWTH,
  personalGroupGrowth: CATEGORY.PERSONAL_GROUP_GROWTH,
  personalGroupGrowthGCR: CATEGORY.PERSONAL_GROUP_GROWTH,
  qcg: CATEGORY.QC_GROWTH,
  qGrowthIncentive: CATEGORY.Q_GROWTH_INCENTIVE,
  earlyQBooster: CATEGORY.Q_GROWTH_INCENTIVE,
  strongStartIncentive: CATEGORY.STRONG_START_INCENTIVE,
  twoTimeCashIncentive: CATEGORY.TWO_TIME_CASH_INCENTIVE,
  visionBonus: CATEGORY.VISION_BONUS,
};

/**
 * Gets the page title and learn more key configuration for a given route path.
 * Used by ComponentLoader to display page titles and learn more links on error pages.
 * @param pathname - The route path to look up
 * @returns Object with i18n key, fallback label, contentKey, learnMoreKey, and tealiumCategory, or undefined if not found
 */
export const getPageTitleForRoute = (
  pathname: string,
  aff?: string,
):
  | { key: string; fallback: string; contentKey?: string; learnMoreKey?: keyof LearnMore; tealiumCategory?: CATEGORY }
  | undefined => {
  const navItem = Object.values(NAV_ITEM_MAP).find((item) => item.path === pathname);
  if (!navItem) return undefined;

  const contentKey = navItem.contentKey ?? navItem.id;
  const isLeadershipSeminar = navItem.id === "leadershipSeminar" || navItem.id === "lsPathView";
  const navigationKey = isLeadershipSeminar ? getNavKeyForIdentity({ id: "leadershipSeminar" }, { aff }) : contentKey;
  const learnMoreKey = LEARN_MORE_KEY_MAP[navItem.id];
  const tealiumCategory = TEALIUM_CATEGORY_MAP[navItem.id];

  return {
    key: `linkTitle.${navigationKey}`,
    fallback: defaultContent.linkTitle[navigationKey as keyof typeof defaultContent.linkTitle] ?? navItem.label,
    contentKey,
    learnMoreKey,
    tealiumCategory,
  };
};

export const createNavigationConfig = (
  response: NavigationApiResponse,
  excludeIds?: string[],
  aff?: string,
): NavigationConfig => {
  const academy: NavItem | null =
    response.Academy?.Id === "academy"
      ? {
          id: "academy",
          label: defaultContent.linkTitle.academy,
          entryPage: "academy",
          path: ROUTE_PATHS.ACADEMY_AUTH,
        }
      : null;

  const academyFlyout: FlyoutMenuItem | null =
    response.Academy?.Id === "academy"
      ? {
          id: "academy",
          label: defaultContent.linkTitle.academy,
          path: ROUTE_PATHS.ACADEMY_AUTH,
        }
      : null;

  const filteredPerformanceIncome = excludeIds
    ? (response.PerformanceIncome ?? []).filter((item) => !excludeIds.includes(item.Id))
    : (response.PerformanceIncome ?? []);

  const performanceIncome = mapItems(filteredPerformanceIncome, aff);
  const groupMgmtNavItems = mapItems(response.GroupManagement?.NavigationItems ?? [], aff);
  const groupMgmtActionReports = mapItems(response.GroupManagement?.ActionReports ?? [], aff);

  const performanceIncomeFlyout = mapItemsToFlyout(filteredPerformanceIncome, aff);
  const groupMgmtNavItemsFlyout = mapItemsToFlyout(response.GroupManagement?.NavigationItems ?? [], aff);

  // Action Reports are fully removed from Group Management navigation.
  const groupManagementFlyout: FlyoutMenuItem[] = groupMgmtNavItemsFlyout;

  return {
    performanceIncome,
    groupMgmtNavItems,
    groupMgmtActionReports,
    academy,
    performanceIncomeFlyout,
    groupManagementFlyout,
    academyFlyout,
  };
};
